/**
 * Page "Emploi du temps des cours" — portail étudiant.
 *
 * Affiche les cours hebdomadaires (EmploiTempsRegulier) de la classe de
 * l'étudiant, avec toggle entre semestre pair (S2/S4/S6) et impair (S1/S3/S5).
 */
import React, { useEffect, useState } from 'react';
import { Calendar, Clock, MapPin, User, Loader2, AlertCircle } from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';

interface Creneau {
  id: number;
  jour: string;
  jour_label: string;
  heure_debut: string;
  heure_fin: string;
  matiere: string;
  code_matiere: string;
  formateur: string;
  salle: string;
  semestre: number | null;
  tronc_commun_groupe: string;
}

const JOURS_ORDRE = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const JOURS_LABEL: Record<string, string> = {
  lundi: 'Lundi', mardi: 'Mardi', mercredi: 'Mercredi',
  jeudi: 'Jeudi', vendredi: 'Vendredi', samedi: 'Samedi',
};

export default function EmploiTempsCours() {
  const [periode, setPeriode] = useState<'pair' | 'impair'>('pair');
  const [creneaux, setCreneaux] = useState<Creneau[]>([]);
  const [etudiant, setEtudiant] = useState<{ classe: string; niveau: string; option: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setError(''); setMessage('');
      try {
        const res = await portailApi.getEdtHebdo(periode);
        if (cancel) return;
        setCreneaux(res.creneaux || []);
        setEtudiant(res.etudiant);
        setMessage(res.message || '');
      } catch (e: any) {
        if (!cancel) setError(e?.message || 'Impossible de charger l\'emploi du temps.');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [periode]);

  // Groupe par jour, trié
  const parJour = JOURS_ORDRE.map(j => ({
    cle: j,
    label: JOURS_LABEL[j] || j,
    creneaux: creneaux
      .filter(c => c.jour?.toLowerCase() === j)
      .sort((a, b) => a.heure_debut.localeCompare(b.heure_debut)),
  })).filter(g => g.creneaux.length > 0);

  // Niveau (L1/L2/L3) pour afficher les bonnes étiquettes S1/S2…
  const niveau = etudiant?.niveau || 'L1';
  const semImpair = niveau === 'L1' ? 1 : niveau === 'L2' ? 3 : 5;
  const semPair = niveau === 'L1' ? 2 : niveau === 'L2' ? 4 : 6;

  return (
    <PortailLayout showHeader title="Emploi du temps des cours">
      <div className="space-y-4">
        {/* Bandeau classe */}
        {etudiant && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-900/40 text-blue-300 flex items-center justify-center">
              <Calendar size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-100 truncate">{etudiant.classe}</div>
              <div className="text-[11px] text-gray-400 truncate">{etudiant.option}</div>
            </div>
          </div>
        )}

        {/* Toggle semestre */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-1 grid grid-cols-2 gap-1">
          <button onClick={() => setPeriode('impair')}
            className={`px-3 py-2 text-sm font-semibold rounded-lg transition ${
              periode === 'impair'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:bg-gray-800'
            }`}>
            Semestre {semImpair} <span className="text-[10px] opacity-70">(impair)</span>
          </button>
          <button onClick={() => setPeriode('pair')}
            className={`px-3 py-2 text-sm font-semibold rounded-lg transition ${
              periode === 'pair'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:bg-gray-800'
            }`}>
            Semestre {semPair} <span className="text-[10px] opacity-70">(pair)</span>
          </button>
        </div>

        {/* Chargement / erreur */}
        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-gray-400 text-sm">
            <Loader2 size={16} className="animate-spin" /> Chargement…
          </div>
        )}

        {error && (
          <div className="bg-red-900/30 border border-red-800 text-red-200 rounded-xl p-4 text-sm flex items-start gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && parJour.length === 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
            <Calendar size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-500" />
            <div className="text-sm font-medium text-gray-300">Aucun cours pour ce semestre</div>
            <p className="text-xs text-gray-500 mt-1.5 max-w-xs mx-auto">
              {message || `L'emploi du temps du semestre ${periode} n'est pas encore saisi pour votre classe.`}
            </p>
          </div>
        )}

        {/* Liste par jour */}
        {!loading && !error && parJour.length > 0 && (
          <div className="space-y-3">
            {parJour.map(j => (
              <section key={j.cle} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <header className="bg-blue-900/30 px-4 py-2 text-xs font-semibold text-blue-200 uppercase tracking-wide flex items-center gap-2">
                  <Calendar size={13} /> {j.label}
                  <span className="ml-auto text-[10px] font-normal text-blue-300/70">
                    {j.creneaux.length} cours
                  </span>
                </header>
                <div className="divide-y divide-gray-800">
                  {j.creneaux.map(c => (
                    <div key={c.id} className="px-4 py-3 flex gap-3">
                      <div className="flex flex-col items-center justify-center min-w-[56px] text-center">
                        <div className="text-sm font-bold text-gray-100">{c.heure_debut}</div>
                        <div className="text-[10px] text-gray-500 my-0.5">↓</div>
                        <div className="text-sm font-bold text-gray-100">{c.heure_fin}</div>
                      </div>
                      <div className="flex-1 min-w-0 border-l border-gray-800 pl-3">
                        <div className="text-sm font-semibold text-white truncate flex items-center gap-2">
                          {c.matiere}
                          {c.tronc_commun_groupe && (
                            <span className="text-[9px] px-1.5 py-0.5 bg-purple-900/40 text-purple-300 rounded">
                              Tronc commun
                            </span>
                          )}
                        </div>
                        {c.formateur && (
                          <div className="text-xs text-gray-300 mt-1 flex items-center gap-1.5">
                            <User size={11} className="text-gray-500" /> {c.formateur}
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          {c.salle && (
                            <span className="text-[11px] text-gray-400 inline-flex items-center gap-1">
                              <MapPin size={10} /> Salle {c.salle}
                            </span>
                          )}
                          {c.code_matiere && (
                            <span className="text-[10px] text-gray-500 font-mono">{c.code_matiere}</span>
                          )}
                          <span className="text-[11px] text-gray-400 inline-flex items-center gap-1 ml-auto">
                            <Clock size={10} /> {timeDiff(c.heure_debut, c.heure_fin)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </PortailLayout>
  );
}

function timeDiff(h1: string, h2: string): string {
  const [a1, b1] = h1.split(':').map(Number);
  const [a2, b2] = h2.split(':').map(Number);
  const mins = (a2 * 60 + b2) - (a1 * 60 + b1);
  if (mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}
